import React, { useState } from 'react';
import { AgentToolCall, AgentCostDetail } from '../types';

const ToolCallCardRawJson: React.FC<{ label: string; json: string }> = ({ label, json }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <button onClick={(e) => { e.stopPropagation(); setShow(!show); }} className="text-[10px] text-blue-600 hover:underline">
        {show ? `▼ ${label}` : `▶ ${label}`}
      </button>
      {show && (
        <pre className="text-xs bg-gray-50 p-2 rounded mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words">
          {(() => { try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json || '(empty)'; } })()}
        </pre>
      )}
    </div>
  );
};

const FolioTermGenerationDetails: React.FC<{ inputJson: string; outputJson: string }> = ({ inputJson, outputJson }) => {
  const input = (() => { try { return JSON.parse(inputJson); } catch { return null; } })();
  const output = (() => { try { return JSON.parse(outputJson); } catch { return null; } })();

  if (!input && !output) return <ToolCallCardRawJson label="Raw" json={inputJson || outputJson} />;

  return (
    <div className="mt-1.5 space-y-1.5">
      {input?.query && (
        <div>
          <span className="text-[10px] font-medium text-gray-500 uppercase">Query</span>
          <p className="text-xs text-gray-700 bg-blue-50 rounded px-2 py-1 mt-0.5">{input.query}</p>
        </div>
      )}
      {output?.terms && output.terms.length > 0 && (
        <div>
          <span className="text-[10px] font-medium text-gray-500 uppercase">Generated Terms ({output.terms.length})</span>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {output.terms.map((term: string, i: number) => (
              <span key={i} className="inline-block bg-purple-100 text-purple-800 text-[11px] px-1.5 py-0.5 rounded">{term}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FolioExpansionDetails: React.FC<{ inputJson: string; outputJson: string }> = ({ inputJson, outputJson }) => {
  const input = (() => { try { return JSON.parse(inputJson); } catch { return null; } })();
  const output = (() => { try { return JSON.parse(outputJson); } catch { return null; } })();

  if (!input && !output) return <ToolCallCardRawJson label="Raw" json={inputJson || outputJson} />;

  return (
    <div className="mt-1.5 space-y-1.5">
      {input?.terms && input.terms.length > 0 && (
        <div>
          <span className="text-[10px] font-medium text-gray-500 uppercase">Queried Terms ({input.terms.length})</span>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {input.terms.map((term: string, i: number) => (
              <span key={i} className="inline-block bg-blue-100 text-blue-800 text-[11px] px-1.5 py-0.5 rounded">{term}</span>
            ))}
          </div>
        </div>
      )}
      {output?.concepts && output.concepts.length > 0 && (
        <div>
          <span className="text-[10px] font-medium text-gray-500 uppercase">Found Concepts ({output.conceptCount ?? output.concepts.length})</span>
          <div className="mt-1 max-h-64 overflow-auto text-[11px]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left text-[10px] text-gray-400 uppercase">
                  <th className="pb-0.5 pr-2">Concept</th>
                  <th className="pb-0.5 pr-2">Relationship</th>
                  <th className="pb-0.5">Definition</th>
                </tr>
              </thead>
              <tbody>
                {output.concepts.map((c: { Label?: string; label?: string; Definition?: string; definition?: string; Relationship?: string; relationship?: string }, i: number) => (
                  <tr key={i} className="border-t border-gray-200 dark:border-gray-700 align-top">
                    <td className="py-1 pr-2 font-medium whitespace-nowrap">{c.Label || c.label}</td>
                    <td className="py-1 pr-2 text-gray-500 whitespace-nowrap">{c.Relationship || c.relationship}</td>
                    <td className="py-1 text-gray-600">{c.Definition || c.definition || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {output?.conceptCount === 0 && (
        <p className="text-xs text-gray-500 italic">No matching concepts found in FOLIO ontology.</p>
      )}
    </div>
  );
};

export const ToolCallCard: React.FC<{ tc: AgentToolCall }> = ({ tc }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRawInput, setShowRawInput] = useState(false);
  const [showRawOutput, setShowRawOutput] = useState(false);

  const formatCostInline = (cost: AgentCostDetail) => {
    const total = (cost.inputTokens / 1_000_000) * cost.costPerMillionInputTokens +
                  (cost.outputTokens / 1_000_000) * cost.costPerMillionOutputTokens;
    return total < 0.01 ? `$${total.toFixed(4)}` : `$${total.toFixed(2)}`;
  };

  const toolDisplayName: Record<string, string> = {
    FolioTermGeneration: '🧠 Term Generation (LLM)',
    FolioExpansion: '📚 FOLIO Ontology Lookup',
  };

  const hasStructuredView = tc.toolName === 'FolioTermGeneration' || tc.toolName === 'FolioExpansion';

  return (
    <div data-component="ToolCallCard" className="bg-white p-2 rounded border border-purple-100 text-xs">
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <span className="font-medium text-purple-800">
          {expanded ? '▼' : '▶'} {toolDisplayName[tc.toolName] || tc.toolName}
        </span>
        <div className="flex items-center gap-2 text-gray-500">
          {tc.modelUsed && <span className="text-[10px] text-gray-400">{tc.modelUsed}</span>}
          {tc.costDetail && <span className="text-green-700">{formatCostInline(tc.costDetail)}</span>}
          <span>{tc.executionTimeMs}ms</span>
        </div>
      </div>
      {expanded && (
        <div className="mt-1.5">
          {tc.toolName === 'FolioTermGeneration' && (
            <FolioTermGenerationDetails inputJson={tc.inputJson} outputJson={tc.outputJson} />
          )}
          {tc.toolName === 'FolioExpansion' && (
            <FolioExpansionDetails inputJson={tc.inputJson} outputJson={tc.outputJson} />
          )}
          {!hasStructuredView && (
            <div className="space-y-1">
              <ToolCallCardRawJson label="Input" json={tc.inputJson} />
              <ToolCallCardRawJson label="Output" json={tc.outputJson} />
            </div>
          )}
          {hasStructuredView && (
            <div className="flex gap-2 mt-2 border-t border-gray-100 pt-1.5">
              <button onClick={(e) => { e.stopPropagation(); setShowRawInput(!showRawInput); }} className="text-[10px] text-gray-400 hover:text-gray-600">
                {showRawInput ? '▼ raw input' : '▶ raw input'}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setShowRawOutput(!showRawOutput); }} className="text-[10px] text-gray-400 hover:text-gray-600">
                {showRawOutput ? '▼ raw output' : '▶ raw output'}
              </button>
            </div>
          )}
          {showRawInput && (
            <pre className="text-[10px] bg-gray-50 p-1.5 rounded mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-gray-500">
              {(() => { try { return JSON.stringify(JSON.parse(tc.inputJson), null, 2); } catch { return tc.inputJson || '(empty)'; } })()}
            </pre>
          )}
          {showRawOutput && (
            <pre className="text-[10px] bg-gray-50 p-1.5 rounded mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-gray-500">
              {(() => { try { return JSON.stringify(JSON.parse(tc.outputJson), null, 2); } catch { return tc.outputJson || '(empty)'; } })()}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};
